import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { functions } from '../../../firebase';
import type { BehaviourService } from './BehaviourPanel';
import type {
  AnomalyEvent,
  AnomalyOverview,
  AnomalyScope,
  LabelAction,
  SaveReview,
  ScanControl,
  SetScanControl,
} from './types';

async function call<T>(requestType: string, payload: unknown): Promise<T> {
  const result = await functions.httpsCallable('settings')({ requestType, payload });
  if (!result.data || result.data.httpErrorCode?.status >= 400)
    throw new Error('Kunne ikke hente eller gemme afvigelser.');
  return result.data as T;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);
const isNullableNumber = (value: unknown) => value === null || isNumber(value);
const isString = (value: unknown): value is string => typeof value === 'string';

// Validate the browser's list boundary without importing backend runtime modules.
function parseOverview(value: unknown): AnomalyOverview {
  const invalid = () => new Error('Svaret med afvigelser er ufuldstændigt. Prøv at opdatere igen.');
  if (
    !isRecord(value) ||
    value.error ||
    value.success === false ||
    !Array.isArray(value.events) ||
    !Array.isArray(value.labels) ||
    !isRecord(value.scan) ||
    !(value.nextCursor === null || (isString(value.nextCursor) && value.nextCursor.length > 0))
  ) {
    throw invalid();
  }
  const validEvents = value.events.every((event: unknown) => {
    if (!isRecord(event) || !isRecord(event.review)) return false;
    return (
      [
        'id',
        'orgId',
        'sensorId',
        'orgTitle',
        'sensorTitle',
        'deviceId',
        'title',
        'description',
        'detailPath',
        'unit',
      ].every((field) => isString(event[field])) &&
      ['FLOW', 'GSM'].includes(String(event.deviceFamily)) &&
      isNumber(event.occurredAt) &&
      isNumber(event.detectedAt) &&
      isNullableNumber(event.expectedMin) &&
      isNullableNumber(event.expectedMax) &&
      Array.isArray(event.points) &&
      event.points.every(
        (point) => isRecord(point) && isNumber(point.at) && isNumber(point.value)
      ) &&
      Array.isArray(event.sourceRefs) &&
      event.sourceRefs.every(isString) &&
      ['pending', 'reviewed', 'investigating'].includes(String(event.review.status)) &&
      (event.review.labelId === null || isString(event.review.labelId)) &&
      isString(event.review.note) &&
      isNumber(event.review.version)
    );
  });
  const validLabels = value.labels.every(
    (label: unknown) =>
      isRecord(label) &&
      isString(label.id) &&
      isString(label.name) &&
      typeof label.archived === 'boolean' &&
      (label.mergedInto === null || isString(label.mergedInto)) &&
      isNumber(label.version)
  );
  const scan = value.scan;
  const validControl =
    scan.control === undefined ||
    (isRecord(scan.control) &&
      typeof scan.control.enabled === 'boolean' &&
      isNumber(scan.control.version) &&
      isNullableNumber(scan.control.updatedAt) &&
      (scan.control.updatedBy === null || isString(scan.control.updatedBy)));
  if (
    !validEvents ||
    !validLabels ||
    !validControl ||
    !isNullableNumber(scan.lastCompletedAt) ||
    !isNullableNumber(scan.lastFailedAt) ||
    !isNumber(scan.deviceCount)
  )
    throw invalid();
  return value as unknown as AnomalyOverview;
}

export const anomalyQueryKey = ['monitoring-anomalies'] as const;
type AnomalyPages = InfiniteData<AnomalyOverview, string | null>;
const behaviourService: BehaviourService = {
  list: () => call('LIST_ANOMALY_PLANTS', {}),
  get: (sensorId, preview) =>
    call('GET_ANOMALY_BEHAVIOUR', { sensorId, ...(preview ? { preview } : {}) }),
  save: (command) => call('SAVE_ANOMALY_BEHAVIOUR', command),
};

export function useAnomalies(scope?: AnomalyScope, enabled = true) {
  const client = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: [...anomalyQueryKey, scope?.orgId ?? null, scope?.sensorId ?? null],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      parseOverview(
        await call<unknown>('GET_ANOMALIES', {
          cursor: pageParam,
          ...(scope ? { orgId: scope.orgId, sensorId: scope.sensorId } : {}),
        })
      ),
    getNextPageParam: (last) => last.nextCursor || undefined,
    staleTime: 60000,
    enabled,
  });
  const pages = query.data?.pages || [];
  const events = Array.from(
    new Map(pages.flatMap((page) => page.events).map((event) => [event.id, event])).values()
  );

  async function save(command: SaveReview) {
    let event: AnomalyEvent;
    try {
      event = await call<AnomalyEvent>('SAVE_ANOMALY_REVIEW', command);
    } catch (error) {
      void client.invalidateQueries({ queryKey: anomalyQueryKey });
      throw error;
    }
    await client.cancelQueries({ queryKey: anomalyQueryKey });
    // An annotation may appear in the fleet queue and several sensor panels at once.
    client.setQueriesData<AnomalyPages>({ queryKey: anomalyQueryKey }, (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              events: page.events.map((item) => (item.id === event.id ? event : item)),
            })),
          }
        : current
    );
    await client.invalidateQueries({ queryKey: anomalyQueryKey });
    return event;
  }

  async function changeLabel(command: LabelAction) {
    await call('CHANGE_ANOMALY_LABEL', command);
    await client.invalidateQueries({ queryKey: anomalyQueryKey });
  }

  async function changeScanControl(command: SetScanControl) {
    try {
      const control = await call<ScanControl>('SET_ANOMALY_SCAN_CONTROL', command);
      await client.cancelQueries({ queryKey: anomalyQueryKey });
      client.setQueriesData<AnomalyPages>({ queryKey: anomalyQueryKey }, (current) =>
        current
          ? {
              ...current,
              pages: current.pages.map((page) => ({ ...page, scan: { ...page.scan, control } })),
            }
          : current
      );
    } finally {
      void client.invalidateQueries({ queryKey: anomalyQueryKey });
    }
  }

  return {
    query,
    events,
    labels: pages[0]?.labels || [],
    scan: pages[0]?.scan,
    save,
    changeLabel,
    changeScanControl,
    behaviourService,
  };
}

export default useAnomalies;
